/**
 * Redstone: the stable API other modules build on. The simulation itself is
 * in sim.ts (one RedstoneSim per world, attached by the world's owner), the
 * power rules in power.ts.
 *
 * `world` is anything with `getBlock` (world/chunk.ts World, which is what
 * `ServerWorld.world` and the client's `ChunkManager.world` are). Everything
 * here is derived from block ids, so it works on any world; scheduling
 * (`notifyNeighbors`) needs the world's RedstoneSim and is a no-op without one.
 */

import { receivedPower, type BlockReader } from './power';
export type { BlockReader };

export type PowerChangeHandler = (world: BlockReader, x: number, y: number, z: number, power: number) => void;
export type ComparatorSourceFn = (world: BlockReader, x: number, y: number, z: number) => number;

/** Block id -> handler called when the power the cell receives changes (0..15). One handler per id, last wins. */
export const POWER_HANDLERS = new Map<number, PowerChangeHandler>();
/** Block id -> comparator reading (0..15) of that block (containers, cake, ...). One per id, last wins. */
export const COMPARATOR_SOURCES = new Map<number, ComparatorSourceFn>();

/** Per-world hooks the RedstoneSim installs (sim.ts `attachRedstone`). */
export interface RedstoneBinding {
  powerAt(x: number, y: number, z: number): number;
  notifyNeighbors(x: number, y: number, z: number): void;
}
export const BINDINGS = new WeakMap<object, RedstoneBinding>();

/** Strongest redstone signal (0..15) the cell at (x, y, z) receives from its six neighbours. */
export function powerAt(world: BlockReader, x: number, y: number, z: number): number {
  const b = BINDINGS.get(world);
  if (b) return b.powerAt(x, y, z);
  return receivedPower(world, x, y, z);
}

/** Does the cell receive any redstone signal? */
export function isPowered(world: BlockReader, x: number, y: number, z: number): boolean {
  return powerAt(world, x, y, z) > 0;
}

/**
 * Call `fn` whenever the power received by a block with one of `blockIds`
 * changes (and once when such a block is placed next to power). `power` is
 * 0..15. The handler may edit the world.
 */
export function onPowerChange(blockIds: readonly number[], fn: PowerChangeHandler): void {
  for (const id of blockIds) POWER_HANDLERS.set(id, fn);
}

/** Register what a comparator reads from blocks with one of `blockIds` (0..15). */
export function comparatorSource(blockIds: readonly number[], fn: ComparatorSourceFn): void {
  for (const id of blockIds) COMPARATOR_SOURCES.set(id, fn);
}

/**
 * Tell the redstone around (x, y, z) that the block there changed in a way
 * its id does not show (a container's contents, say): comparators and other
 * components beside it re-evaluate on the next tick.
 */
export function notifyNeighbors(world: BlockReader, x: number, y: number, z: number): void {
  BINDINGS.get(world)?.notifyNeighbors(x, y, z);
}
